/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { shouldShowOverlay, getFillAndStroke } from './MapUnitHelpers';
import { getContrastTextColorValue } from '../../../utils/common';

/**
 * Generates a unique pattern ID based on fill color and overlay color
 */
export const getPatternId = (fillColor, overlayColor) => {
  const sanitizedFill = fillColor.replace(/[^a-zA-Z0-9]/g, '');
  return `unitOverlayPattern-${sanitizedFill}-${overlayColor}`;
};

/**
 * Creates an SVG pattern that combines the unit's fill color with diagonal stripes
 * OPTIMIZED: Small pattern size (4x4) for better performance
 */
const createCompositePattern = (fillColor, overlayColor, patternId) => {
  const stripeColor = overlayColor === 'white' ? 'white' : 'black';
  // Very subtle stripes - just a hint of overlay
  const stripeOpacity = overlayColor === 'white' ? '0.12' : '0.1';

  // PERFORMANCE: Reduced from 8x8 to 4x4 pixels
  // Smaller patterns = less GPU memory, faster rendering, finer appearance
  return (
    <pattern
      key={patternId}
      id={patternId}
      patternUnits="userSpaceOnUse"
      width="4"
      height="4"
      patternTransform="rotate(45)"
    >
      {/* Background rect with unit's color */}
      <rect width="4" height="4" fill={fillColor} />

      {/* Diagonal stripes overlay - proportionally smaller */}
      <rect
        width="1.5"
        height="4"
        fill={stripeColor}
        opacity={stripeOpacity}
      />
    </pattern>
  );
};

/**
 * Component that renders all dynamic patterns needed by visible units
 * Should be placed in the SVG <defs> section
 *
 * Performance: Patterns are only regenerated when the set of unique colors changes,
 * not when units change displayMode or other properties.
 */
const MapUnitPatterns = ({
  units, platformShowOccupancy, unitFill, unitStroke,
}) => {
  // Extract unique (fill, overlayColor) pairs that need patterns
  // Must match the logic in MapUnitHelpers.generateUnitShapesMarkup
  const uniquePatternKeys = useMemo(() => {
    if (!units || units.length === 0) return new Set();

    const patternKeySet = new Set();

    units.forEach((unit) => {
      const { displayMode, info } = unit;
      const { residence_status: status } = info || {};
      // Match the fallback logic in MapUnitHelpers
      const keyColor = info?.key_color || unitFill;

      // Use the actual shouldShowOverlay logic
      const showOverlay = shouldShowOverlay(status, platformShowOccupancy, displayMode);

      if (showOverlay) {
        // Get the actual fill color (same logic as MapUnitHelpers)
        const { fill } = getFillAndStroke(displayMode, keyColor, unitStroke, unitFill);
        // Inactive units always use black overlay for consistent appearance
        // Active units use pre-computed contrast_overlay from API, fallback to computed value
        const overlayColor = displayMode === 'inactive'
          ? 'black'
          : (info?.contrast_overlay || getContrastTextColorValue(fill) || 'white');
        // Store as "fill|overlayColor" to track unique combinations
        patternKeySet.add(`${fill}|${overlayColor}`);
      }
    });

    return patternKeySet;
  }, [units, platformShowOccupancy, unitFill, unitStroke]);

  // Convert Set to sorted array for stable memoization key
  const patternKeyArray = useMemo(
    () => Array.from(uniquePatternKeys).sort(),
    [uniquePatternKeys],
  );

  // Create hash for stable comparison (only regenerate when patterns change)
  const patternHash = useMemo(
    () => patternKeyArray.join(','),
    [patternKeyArray],
  );

  // Generate patterns only when unique combinations change
  const patterns = useMemo(() => {
    if (patternKeyArray.length === 0) return [];

    return patternKeyArray.map((key) => {
      const [fill, overlayColor] = key.split('|');
      const patternId = getPatternId(fill, overlayColor);
      return createCompositePattern(fill, overlayColor, patternId);
    });
  }, [patternHash]); // Only depend on pattern hash, not the entire units array

  return patterns.length > 0 ? <>{patterns}</> : null;
};

export default MapUnitPatterns;
